import { useEffect, useId, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { extractApiErrorMessage } from '../../utils/apiError';

export interface InlineEditTextProps {
  value: string;
  /** Saves the new text; a rejection keeps the field open with the reason. */
  onSave: (next: string) => Promise<unknown>;
  /** Accessible name of the field, e.g. "Event name". */
  label: string;
  /** Text styling, shared by the read and edit states so nothing jumps. */
  className?: string;
  maxLength?: number;
}

/**
 * Text that becomes an input when clicked: Enter or leaving the field saves,
 * Escape puts it back. Empty text is never saved. A save that fails leaves the
 * field open with the reason under it, so it can be retried or abandoned.
 */
export function InlineEditText({
  value,
  onSave,
  label,
  className = '',
  maxLength = 255,
}: InlineEditTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const errorId = useId();
  const cancelled = useRef(false);
  // In flight or already saved: a blur that follows Enter, or the input being
  // removed once it closes, must not save the same text a second time.
  const busy = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    busy.current = false;
    setError('');
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing]);

  const commit = async () => {
    if (cancelled.current) {
      cancelled.current = false;
      setError('');
      setEditing(false);
      return;
    }
    if (busy.current) return;
    const next = draft.trim();
    if (!next || next === value) {
      setDraft(value);
      setError('');
      setEditing(false);
      return;
    }
    busy.current = true;
    setSaving(true);
    try {
      await onSave(next);
      setError('');
      setEditing(false);
    } catch (err) {
      busy.current = false;
      setError(extractApiErrorMessage(err, 'Could not save — press Enter to retry or Esc to undo'));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex flex-col min-w-0 w-full max-w-xl">
        <input
          ref={inputRef}
          aria-label={label}
          value={draft}
          maxLength={maxLength}
          // Read-only rather than disabled while saving: disabling a focused
          // input can blur it, which would start a second save.
          readOnly={saving}
          aria-busy={saving || undefined}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Saves in place, so a failure leaves the cursor in the field.
              e.preventDefault();
              void commit();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              // A save already sent cannot be taken back, so Escape waits it
              // out rather than looking like it cancelled the rename.
              if (busy.current) return;
              cancelled.current = true;
              inputRef.current?.blur();
            }
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={`${className} min-w-0 w-full bg-white border rounded-lg px-2 -mx-2 focus:outline-none focus:ring-2 ${
            error
              ? 'border-red-400 focus:ring-red-300'
              : 'border-primary-300 focus:ring-primary-400'
          }`}
        />
        {error && (
          <span
            id={errorId}
            role="alert"
            className="mt-1 text-xs font-normal tracking-normal text-red-700"
          >
            {error}
          </span>
        )}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={`Click to edit the ${label.toLowerCase()}`}
      className={`${className} group inline-flex items-center gap-2 text-left rounded-lg px-2 -mx-2 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400`}
    >
      {value}
      <Pencil
        size={14}
        className="text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden
      />
    </button>
  );
}
